import {
  Approval,
  Cancel,
  CheckCircle,
  Description,
  DocumentScanner,
  History,
  MoreVert,
  Upload,
  WarningAmber,
  WhatsApp,
  AttachMoney,
  Share,
  HourglassEmpty,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState, useRef } from "react";

import { isAfter } from "date-fns/isAfter";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storageService } from "../firebase-config";
import { User } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import { soulManager } from "../data/managers/SoulManager";
import {
  CreditApplication,
  getApprovedTotals,
} from "../data/models/applications/CreditApplication";
import {
  calculateMonthlyInstallment,
  getInstallmentStatusLabel,
  InstallmentApplication,
} from "../data/models/applications/InstallmentApplication";
import { PaymentDialog } from "../data/models/applications/PaymentDialog";
import ApproveCreditDialog from "./ApprovedCreditDialog";
import ConfirmationDialog from "./dialogs/ConfirmationDialog";
import { ExportPDFButton } from "./ExportPDFButton";
import html2canvas from "html2canvas";
import { FinalizeCreditDialog } from "./FinalizeCreditDialog";
import UploadImageButton from "./UploadImageButton";
import { WhatsAppNotifyButton } from "./WhatsAppNotifyButton";

const GREEN = "#00C853";
const DARK = "#0D1420";
const WHITE = "#FFFFFF";

const STATUS_CONFIG: Record<string, { color: string; label: string; bg: string }> = {
  APPROVED: { color: GREEN, label: "Aprobada", bg: "#00C85318" },
  PENDING: { color: "#f59e0b", label: "Pendiente", bg: "#f59e0b18" },
  FINISHED: { color: "#8899AA", label: "Finalizada", bg: "#8899AA18" },
  DECLINED: { color: "#ef4444", label: "Rechazada", bg: "#ef444418" },
};

const fmtDate = (str: string) => {
  const d = new Date(str);
  return d.toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" });
};

const getDaysLeft = (dateStr: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 3600 * 24));
};

export function ApplicationCard({
  row,
  fmtMoney,
  StatusChip,
  open,
  onToggle,
  uploadConsent,
  uploadInstallment,
  user,
  loadingDefault,
  enableConsent,
  declineApplication,
  loadApplications,
  isAdmin = false,
  updateInstallment,
  finalizeApplication,
}: {
  row: CreditApplication;
  fmtMoney: (value: number) => string;
  StatusChip: React.ComponentType<any>;
  open: boolean;
  onToggle: () => void;
  uploadConsent?: (id: string, url: string) => Promise<void> | undefined;
  finalizeApplication?: (id: string, qualification: number) => Promise<boolean> | undefined;
  uploadInstallment?: (applicationId: string, installmentId: string, url: string) => Promise<void>;
  user: User;
  loadingDefault?: boolean;
  enableConsent?: (id: string) => Promise<void> | undefined;
  declineApplication?: (id: string) => Promise<void> | undefined;
  loadApplications?: () => Promise<void>;
  isAdmin?: boolean;
  updateInstallment?: (creditId: string, installmentId: string, interestAmount: number, capitalAmount: number, isPaymentInstallment: boolean, rejectReason: string | undefined, isRejected: boolean) => Promise<void>;
}) {
  const [openApproved, setOpenApproved] = useState(false);
  const [openDecline, setOpenDecline] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [openClientInfo, setOpenClientInfo] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openFinalize, setOpenFinalize] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInstallment, setSelectedInstallment] = useState<InstallmentApplication | null>(null);

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      setSharing(true);
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#ffffff", scale: 2 });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], `credito-${row.applicantName}.png`, { type: "image/png" });
        if (navigator.share && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Crédito ${row.applicantName}` });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `credito-${row.applicantName}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    } catch (err) {
      console.error("Error compartiendo:", err);
    } finally {
      setSharing(false);
    }
  };

  const handleApprove = async ({ creditId, guarantorId, interestRate, approvalDate, updates }: { creditId: string; guarantorId: string; interestRate: number; approvalDate: string; updates: Partial<CreditApplication>; }) => {
    const approval = new Date(approvalDate);
    approval.setMonth(approval.getMonth() + 1);
    const paymentDate = approval.toISOString();
    const base: Partial<CreditApplication> = { ...updates, id: creditId, interestRate, approvalDate, paymentDate, guarantorId, status: "APPROVED" };
    const result = await soulManager.updateSoulApplicationByAdmin(base);
    if (!result) throw new Error("Error en handleApprove");

    // Crear installment de interés adelantado automáticamente
    if (updates.amount && interestRate && updateInstallment) {
      const interesAdelantado = Math.round(updates.amount * (interestRate / 100));
      const installmentId = uuidv4();
      // Paso 1: crear el installment
      await soulManager.createInstallmentApplication({
        applicationId: creditId,
        installmentId,
        url: "",
      });
      // Paso 2: aprobarlo con el interés adelantado
      await updateInstallment(creditId, installmentId, interesAdelantado, 0, false, undefined, false);
    }

    await loadApplications!();
  };

  const subtotals = getApprovedTotals(row);
  const paymentLoanDate = new Date(row.paymentDate);
  paymentLoanDate.setHours(0, 0, 0, 0);
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const paymentLoan = row.status !== "FINISHED" && (isAfter(todayMidnight, paymentLoanDate) || todayMidnight.getTime() === paymentLoanDate.getTime());
  const mostrarMora = paymentLoan && !row.moraAnulada;
  const statusCfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.PENDING;
  // Contar pagos de capital ya hechos
  const pagosCapitalAprobados = row.installments
    ? row.installments.filter(i => i.installmentStatus === "APPROVED" && (i.capitalAmount ?? 0) > 0).length
    : 0;
  const esUltimaCuota = row.termMonths === 1 || pagosCapitalAprobados >= row.termMonths - 1;
  const capitalPorCuota = Math.round(row.amount / row.termMonths);
  const interesPorCuota = Math.round(row.amount * (row.interestRate / 100));
  const esInterestOnly = row.paymentType === "INTEREST_ONLY";
  const monthlyInstallment = esUltimaCuota
    ? capitalPorCuota  // última cuota: solo capital
    : esInterestOnly
      ? interesPorCuota  // solo interés mensual
      : calculateMonthlyInstallment(row.amount, row.interestRate, row.termMonths);
  const progressPct = row.amount > 0 ? Math.min(100, Math.round((subtotals.totalApproved / row.amount) * 100)) : 0;
  const daysLeft = row.paymentDate ? getDaysLeft(row.paymentDate) : null;

  // Mora acumulada: $5.000 por día vencido
  const MORA_POR_DIA = 5000;
  const diasMora = daysLeft !== null && daysLeft < 0 ? Math.abs(daysLeft) : 0;
  const moraAcumulada = diasMora * MORA_POR_DIA;

  // Último pago aprobado
  const lastPayment = row.installments
    ? [...row.installments]
        .filter(i => i.installmentStatus === "APPROVED")
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    : null;

  // WhatsApp mora
  const whatsappMoraLink = () => {
    const phone = row.phoneOptional?.replace(/\D/g, "") || "";
    const payDate = row.paymentDate ? new Date(row.paymentDate).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }) : "";
    const msg = encodeURIComponent(`Hola *${row.applicantName.toUpperCase()}* 👋, tu pago en Prestiya se venció el *${payDate.toUpperCase()}* y tienes *${diasMora} DÍA(S) de mora*. Por favor realiza el pago y antes de transferir *CONSÚLTANOS LA CUENTA* para evitar errores. Si ya pagaste, ignora este mensaje. 🤝`);
    return `https://wa.me/${phone}?text=${msg}`;
  };

  const isApprovedOrFinished = row.status === "APPROVED" || row.status === "FINISHED";

  return (
    <>
      <Box ref={cardRef}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${statusCfg.color}44`,
          borderLeft: `4px solid ${statusCfg.color}`,
          transition: "box-shadow 0.2s ease",
          "&:hover": { boxShadow: `0 4px 20px ${statusCfg.color}22` },
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.5}>

            {/* Header — nombre + estado */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box flex={1} minWidth={0}>
                <Typography onClick={() => setOpenClientInfo(true)} sx={{ fontWeight: 700, fontSize: 15, color: DARK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer", "&:hover": { color: GREEN } }}>
                  {row.applicantName}
                </Typography>
                {row.guarantorName && (
                  <Typography sx={{ fontSize: 11, color: "#8899AA" }}>Avalista: {row.guarantorName}</Typography>
                )}
              </Box>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Chip label={statusCfg.label} size="small" sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 700, fontSize: 11, border: `1px solid ${statusCfg.color}44`, height: 24 }} />
                {row.isConsentAvailable && <Description sx={{ color: GREEN, fontSize: 18 }} />}
                {isAdmin && (
                  <>
                    <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                      PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.12)", minWidth: 200, overflow: "hidden" } }}
                    >
                      {row.status === "APPROVED" && (
                        <MenuItem
                          onClick={() => { setAnchorEl(null); setOpenDecline(true); }}
                          sx={{ py: 1.5, px: 2, "&:hover": { bgcolor: "#ef444411" } }}
                        >
                          <ListItemIcon><Cancel fontSize="small" sx={{ color: "#ef4444" }} /></ListItemIcon>
                          <ListItemText primary="Declinar solicitud" primaryTypographyProps={{ fontSize: 14, fontWeight: 600, color: "#ef4444" }} />
                        </MenuItem>
                      )}
                      {row.status === "PENDING" && (
                        <MenuItem
                          onClick={() => { setAnchorEl(null); setOpenDecline(true); }}
                          sx={{ py: 1.5, px: 2, "&:hover": { bgcolor: "#ef444411" } }}
                        >
                          <ListItemIcon><Cancel fontSize="small" sx={{ color: "#ef4444" }} /></ListItemIcon>
                          <ListItemText primary="Declinar solicitud" primaryTypographyProps={{ fontSize: 14, fontWeight: 600, color: "#ef4444" }} />
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </Stack>
            </Stack>

            {/* Monto grande */}
            {isApprovedOrFinished && (
              <>
                <Typography sx={{ fontWeight: 900, fontSize: 28, color: DARK, lineHeight: 1 }}>
                  {fmtMoney(row.amount)}
                </Typography>

                {/* Pagado + Saldo */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: `${GREEN}11`, borderRadius: 2, p: 1.2, border: `1px solid ${GREEN}33` }}>
                      <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Pagado</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: GREEN }}>{fmtMoney(subtotals.totalApproved)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: "#ef444411", borderRadius: 2, p: 1.2, border: "1px solid #ef444433" }}>
                      <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Saldo</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#ef4444" }}>{fmtMoney(subtotals.balance)}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Barra de progreso */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: "#8899AA" }}>Progreso de pago</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: GREEN }}>{progressPct}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={progressPct} sx={{ height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: GREEN, borderRadius: 3 } }} />
                </Box>

                {/* Último pago */}
                {lastPayment && (
                  <Box sx={{ bgcolor: "#f7f9f7", borderRadius: 2, p: 1.2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Último pago</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK }}>{fmtDate(lastPayment.createdAt)}</Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: 10, color: "#8899AA" }}>Capital + Interés</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>
                          {fmtMoney((lastPayment.capitalAmount || 0) + (lastPayment.interestAmount || 0))}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}

                {/* Próxima cuota */}
                {row.status !== "FINISHED" && <Box sx={{ bgcolor: paymentLoan ? "#ef444411" : `${GREEN}11`, borderRadius: 2, p: 1.2, border: `1px solid ${paymentLoan ? "#ef444433" : GREEN + "33"}` }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Próxima cuota</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: paymentLoan ? "#ef4444" : DARK }}>
                        {fmtDate(row.paymentDate)}
                        {paymentLoan && <WarningAmber sx={{ fontSize: 14, color: "#ef4444", ml: 0.5, verticalAlign: "middle" }} />}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: paymentLoan ? "#ef4444" : GREEN }}>{fmtMoney(monthlyInstallment)}</Typography>
                      <Typography sx={{ fontSize: 10, color: "#8899AA" }}>
                        {daysLeft !== null ? (daysLeft < 0 ? `${Math.abs(daysLeft)} días vencida` : daysLeft === 0 ? "Hoy" : `faltan ${daysLeft} días`) : ""}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>}

                {/* Mora acumulada */}
                {mostrarMora && isAdmin && moraAcumulada > 0 && (
                  <Box sx={{ bgcolor: "#ef444411", borderRadius: 2, p: 1.5, border: "1px solid #ef444433" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#8899AA", textTransform: "uppercase", letterSpacing: 1 }}>Mora acumulada</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#ef4444" }}>{fmtMoney(moraAcumulada)}</Typography>
                        <Typography sx={{ fontSize: 11, color: "#8899AA" }}>{diasMora} días × $5.000/día</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth size="small" variant="contained"
                        onClick={async () => {
                          if (!updateInstallment) return;
                          const installmentId = uuidv4();
                          // Paso 1: crear el installment
                          await soulManager.createInstallmentApplication({
                            applicationId: row.id!,
                            installmentId,
                            url: "",
                          });
                          // Paso 2: aprobarlo con el valor de la mora
                          await updateInstallment(row.id!, installmentId, moraAcumulada, 0, false, undefined, false);
                          await loadApplications?.();
                        }}
                        sx={{ bgcolor: "#ef4444", color: "white", fontWeight: 700, borderRadius: "50px", textTransform: "none", fontSize: 12, boxShadow: "none", "&:hover": { bgcolor: "#dc2626" } }}
                      >
                        Cobrar mora
                      </Button>
                      <Button
                        fullWidth size="small" variant="outlined"
                        onClick={async () => {
                          await soulManager.anularMora(row.id!);
                          await loadApplications?.();
                        }}
                        sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, fontSize: 12, borderColor: "#ef444433", color: "#ef4444" }}
                      >
                        Anular mora
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* Botón compartir */}
                {row.status === "APPROVED" && isAdmin && (
                  <Button
                    fullWidth size="small" variant="outlined"
                    onClick={handleShare}
                    disabled={sharing}
                    startIcon={sharing ? <CircularProgress size={14} sx={{ color: GREEN }} /> : <Share sx={{ fontSize: 16 }} />}
                    sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, fontSize: 12, borderColor: `${GREEN}44`, color: GREEN, mt: 0.5 }}
                  >
                    {sharing ? "Generando..." : "Compartir tarjeta"}
                  </Button>
                )}

                {/* Botón mora WhatsApp */}
                {paymentLoan && isAdmin && (
                  <Button fullWidth variant="contained" startIcon={<WhatsApp />} href={whatsappMoraLink()} target="_blank" rel="noopener"
                    sx={{ bgcolor: "#25D366", color: "white", fontWeight: 700, borderRadius: "50px", py: 1, fontSize: 13, textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#1ebe5a" } }}>
                    Notificar mora por WhatsApp
                  </Button>
                )}

                {/* Botones acción */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" startIcon={<History />} onClick={() => setOpenHistory(true)}
                      sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, fontSize: 12, borderColor: "#e0e0e0", color: DARK, "&:hover": { borderColor: DARK } }}>
                      Ver historial
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    {row.status === "APPROVED" && <UploadPaymentButton row={row} user={user} uploadInstallment={uploadInstallment} />}
                  </Grid>
                </Grid>
              </>
            )}

            {/* Tarjetas PENDING */}
            {row.status === "PENDING" && (
              <>
                <Typography sx={{ fontWeight: 900, fontSize: 28, color: DARK, lineHeight: 1 }}>
                  {fmtMoney(row.amount)}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: "#f7f9f7", borderRadius: 2, p: 1.2 }}>
                      <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Plazo</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK }}>{row.termMonths} meses</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: "#f7f9f7", borderRadius: 2, p: 1.2 }}>
                      <Typography sx={{ fontSize: 10, color: "#8899AA", fontWeight: 600, textTransform: "uppercase" }}>Propósito</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.purpose}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {row.status === "PENDING" && (
                  <Typography sx={{ fontSize: 12, color: "#667788" }}>
                    {row.accountBank} · {row.accountType} · {row.accountNumber}
                  </Typography>
                )}

                {row.consentPdfUploaded && row.status === "PENDING" && isAdmin && (
                  <Link href={row.consentPdfUploaded} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 13 }}>
                    Ver consentimiento firmado
                  </Link>
                )}

                {row.isConsentAvailable && row.status === "PENDING" && !row.consentPdfUploaded && (
                  <>
                    <ExportPDFButton row={row} />
                    <UploadImageButton
                      path={`users/${user.uid}/consent/${row.id}.jpg`}
                      label="Subir pagaré firmado"
                      color="green"
                      onUploaded={async (url) => {
                        if (!uploadConsent) return;
                        await uploadConsent(row.id!, url);
                        await loadApplications?.();
                      }}
                    />
                  </>
                )}
                {row.isConsentAvailable && row.status === "PENDING" && row.consentPdfUploaded && isAdmin && (
                  <ExportPDFButton row={row} />
                )}

                {!row.isConsentAvailable && row.consentPdfUploaded && row.status === "PENDING" && !isAdmin && (
                  <Typography variant="body2" color="textSecondary" sx={{ border: 1, borderRadius: 3, borderColor: "gainsboro", p: 1, fontSize: 12 }}>
                    Los documentos serán revisados y se le notificará sobre los avances.
                  </Typography>
                )}

                {row.status === "PENDING" && !row.consentPdfUploaded && !row.isConsentAvailable && isAdmin && (
                  <Button onClick={() => enableConsent!(row.id!)} fullWidth variant="contained" startIcon={<DocumentScanner />}
                    sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: DARK, color: "white", boxShadow: "none" }} disabled={loadingDefault}>
                    Habilitar Pagaré
                  </Button>
                )}

                {row.status === "PENDING" && isAdmin && (
                  <Button onClick={() => setOpenDecline(true)} fullWidth variant="outlined" startIcon={<Cancel />} color="error"
                    sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700 }} disabled={loadingDefault}>
                    Rechazar Solicitud
                  </Button>
                )}

                {row.status === "PENDING" && row.consentPdfUploaded && isAdmin && (
                  <Button onClick={() => setOpenApproved(true)} fullWidth variant="contained" startIcon={<Approval />}
                    sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, color: "white", boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}>
                    Aprobar crédito
                  </Button>
                )}

                <ApproveCreditDialog open={openApproved} credit={row} onClose={() => setOpenApproved(false)} onApprove={handleApprove} />
              </>
            )}

            {/* Finalizar crédito */}
            {row.status === "APPROVED" && subtotals.balance === 0 && isAdmin && (
              <Button onClick={() => setOpenFinalize(true)} fullWidth variant="contained" startIcon={<CheckCircle />}
                sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, color: "white", boxShadow: "none" }}>
                Finalizar crédito
              </Button>
            )}

            <FinalizeCreditDialog open={openFinalize} onClose={() => setOpenFinalize(false)} credit={row} finalizeApplication={finalizeApplication} />

          </Stack>
        </CardContent>
      </Card>

      {/* Dialog historial de pagos */}
      <Dialog open={openHistory} onClose={() => setOpenHistory(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ bgcolor: DARK, color: "white", fontWeight: 700, fontSize: 16, py: 2 }}>
          Historial de pagos — {row.applicantName}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {row.installments && row.installments.length > 0 ? (
            <Box>
                  {[...row.installments]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((inst) => (
                      <Box
                        key={inst.installmentId}
                        onClick={() => {
                          if (inst.installmentStatus === "PENDING" && isAdmin) {
                            setSelectedInstallment(inst);
                            setOpenDialog(true);
                          }
                        }}
                        sx={{ mb: 1.5, p: 1.5, bgcolor: inst.installmentStatus === "PENDING" && isAdmin ? "white" : "#f7f9f7", borderRadius: 2, border: `1px solid ${inst.installmentStatus === "APPROVED" ? GREEN + "33" : inst.installmentStatus === "PENDING" ? "#f59e0b44" : "#ef444433"}`, cursor: inst.installmentStatus === "PENDING" && isAdmin ? "pointer" : "default", "&:hover": inst.installmentStatus === "PENDING" && isAdmin ? { boxShadow: "0 2px 8px #f59e0b22" } : {} }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0D1420" }}>{fmtDate(inst.createdAt)}</Typography>
                            <Chip
                              label={inst.installmentStatus === "PENDING" && isAdmin ? "Toca para aprobar" : getInstallmentStatusLabel(inst.installmentStatus)}
                              size="small"
                              sx={{ height: 18, fontSize: 10, fontWeight: 700, mt: 0.3, bgcolor: inst.installmentStatus === "APPROVED" ? `${GREEN}18` : inst.installmentStatus === "PENDING" ? "#f59e0b18" : "#ef444418", color: inst.installmentStatus === "APPROVED" ? GREEN : inst.installmentStatus === "PENDING" ? "#f59e0b" : "#ef4444" }}
                            />
                          </Box>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{ textAlign: "right" }}>
                              <Typography sx={{ fontSize: 10, color: "#8899AA" }}>Capital</Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: GREEN }}>{inst.capitalAmount ? fmtMoney(inst.capitalAmount) : "—"}</Typography>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                              <Typography sx={{ fontSize: 10, color: "#8899AA" }}>Interés</Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>{inst.interestAmount ? fmtMoney(inst.interestAmount) : "—"}</Typography>
                            </Box>
                          </Stack>
                        </Stack>
                        {inst.url && (
                          <Box
                            component="a"
                            href={inst.url}
                            target="_blank"
                            rel="noopener"
                            sx={{ display: "block", borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0", textDecoration: "none" }}
                          >
                            <Box
                              component="img"
                              src={inst.url}
                              alt="Comprobante"
                              sx={{ width: "100%", maxHeight: 200, objectFit: "cover", display: "block" }}
                            />
                            <Typography sx={{ fontSize: 11, color: "#8899AA", textAlign: "center", py: 0.5, bgcolor: "white" }}>
                              Ver comprobante completo →
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#8899AA", textAlign: "center", py: 3, fontSize: 14 }}>No hay pagos registrados</Typography>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={openDecline}
        title="Declinar solicitud"
        message={`¿Estás seguro que querés declinar la solicitud de ${row.applicantName}? Esta acción no se puede deshacer.`}
        confirmText="Declinar"
        cancelText="Cancelar"
        danger={true}
        onClose={() => setOpenDecline(false)}
        onConfirm={async () => { await declineApplication!(row.id!); setOpenDecline(false); }}
      />

      {/* Dialog info cliente */}
      <Dialog open={openClientInfo} onClose={() => setOpenClientInfo(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
        <Box sx={{ bgcolor: "#0D1420", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, color: "white" }}>Información del cliente</Typography>
          <IconButton onClick={() => setOpenClientInfo(false)} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, bgcolor: "#f7f9f7" }}>
          <Stack spacing={1.5}>
            {[
              { label: "Nombre", value: row.applicantName },
              { label: "Documento", value: `${row.documentType || ""} ${row.document || ""}` },
              { label: "Teléfono", value: row.phoneOptional },
              { label: "Email", value: row.emailOptional },
              { label: "Banco", value: row.accountBank },
              { label: "Tipo de cuenta", value: row.accountType },
              { label: "Número de cuenta", value: row.accountNumber },
              { label: "Avalista", value: row.guarantorName },
              { label: "Tel. avalista", value: row.guarantorPhone },
            ].filter((item) => item.value).map((item) => (
              <Box key={item.label} sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 12, color: "#8899AA", fontWeight: 600 }}>{item.label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0D1420", textAlign: "right", maxWidth: "60%" }}>{item.value}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Dialog>

      </Box>
      {selectedInstallment && (
        <PaymentDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          installment={selectedInstallment}
          isAdmin={isAdmin}
          totalAmount={Math.round(calculateMonthlyInstallment(row.amount, row.interestRate, row.termMonths))}
          interestAmount={Math.round(row.amount * (row.interestRate / 100))}
          capitalAmount={Math.round(row.amount / row.termMonths)}
          onSubmit={async ({ interestAmount, capitalAmount, isPaymentInstallment, rejectReason, isRejected }) => {
            updateInstallment!(row.id!, selectedInstallment.installmentId, interestAmount, capitalAmount, isPaymentInstallment, rejectReason, isRejected);
          }}
        />
      )}
    </>
  );
}

// Botón de subir pago inline — mismo estilo que Ver historial
function UploadPaymentButton({ row, user, uploadInstallment }: {
  row: CreditApplication;
  user: User;
  uploadInstallment?: (applicationId: string, installmentId: string, url: string) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const installmentId = React.useRef(uuidv4());

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const fileRef = ref(storageService, `users/${user.uid}/installments/${row.id}/${installmentId.current}.jpg`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      if (uploadInstallment) await uploadInstallment(row.id!, installmentId.current, url);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
      <Button
        fullWidth
        variant="contained"
        startIcon={success ? <CheckCircle /> : <Upload />}
        onClick={() => inputRef.current?.click()}
        disabled={loading}
        sx={{
          bgcolor: "#00C853",
          color: "white",
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 600,
          fontSize: 12,
          borderRadius: "50px",
          textTransform: "none",
          boxShadow: "none",
          py: "6px",
          "&:hover": { bgcolor: "#00b048", boxShadow: "none" },
          "&:disabled": { bgcolor: "#cccccc" },
        }}
      >
        {loading ? "Subiendo..." : success ? "¡Listo!" : "Subir pago"}
      </Button>
    </>
  );
}

